<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card ledger-card">
          <div class="card-body">
            <div class="ledger-header">
              <div>
                <h5>Ledger</h5>
                <p>Sales, purchases, and confirmed return entries in one finance view.</p>
              </div>
              <div class="ledger-tabs">
                <button type="button" :class="{ active: viewMode === 'ledger' }" @click="switchView('ledger')">Ledger</button>
                <button type="button" :class="{ active: viewMode === 'credits' }" @click="switchView('credits')">Customer Credits</button>
              </div>
            </div>

            <div class="ledger-toolbar">
              <div class="row g-3 align-items-end">
                <div class="col-12 col-md-6 col-lg-2">
                  <label>From Date</label>
                  <b-form-input v-model="filters.date_from" type="date" />
                </div>
                <div class="col-12 col-md-6 col-lg-2">
                  <label>To Date</label>
                  <b-form-input v-model="filters.date_to" type="date" />
                </div>
                <div class="col-12 col-md-6 col-lg-2">
                  <label>Type</label>
                  <b-form-select v-model="filters.transaction_type" :options="transactionTypeOptions" />
                </div>
                <div class="col-12 col-md-6 col-lg-2">
                  <label>Location</label>
                  <b-form-select v-model="filters.locationid" :options="locationOptions" />
                </div>
                <div class="col-12 col-md-6 col-lg-2">
                  <label>Search</label>
                  <b-form-input v-model="filters.search" type="search" placeholder="Bill, account, payment" @keyup.enter="applyFilters" />
                </div>
                <div class="col-12 col-md-6 col-lg-2">
                  <div class="d-flex gap-2 ledger-actions">
                    <button class="btn btn-submit ledger-action-btn" type="button" @click="applyFilters">
                      View
                    </button>
                    <button class="btn btn-outline-secondary ledger-action-btn" type="button" @click="resetFilters">
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="summary-grid">
              <div class="summary-card debit">
                <span>Total Debit</span>
                <strong>{{ formatCurrency(summary.total_debit) }}</strong>
              </div>
              <div class="summary-card credit">
                <span>Total Credit</span>
                <strong>{{ formatCurrency(summary.total_credit) }}</strong>
              </div>
              <div class="summary-card balance">
                <span>Closing Balance</span>
                <strong>{{ formatCurrency(summary.closing_balance) }}</strong>
              </div>
            </div>

            <div v-if="viewMode === 'ledger'" class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="rows"
                :loading="loading"
                :pagination="false"
                row-key="entryid"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'transaction_date'">
                    {{ formatDate(record.transaction_date) }}
                  </template>
                  <template v-else-if="column.key === 'transaction_type'">
                    <span class="type-chip">{{ transactionLabel(record.transaction_type) }}</span>
                  </template>
                  <template v-else-if="column.key === 'reference'">
                    <div class="reference-cell">
                      <strong>{{ record.billno || record.referenceid }}</strong>
                      <small>{{ record.referenceid }}</small>
                    </div>
                  </template>
                  <template v-else-if="column.key === 'account_name'">
                    <div class="account-cell">
                      <strong>{{ record.account_name || "-" }}</strong>
                      <small>{{ record.account_type || "-" }}</small>
                    </div>
                  </template>
                  <template v-else-if="['debit', 'credit'].includes(column.key)">
                    <span class="money-text">{{ formatCurrency(record[column.key]) }}</span>
                  </template>
                  <template v-else>
                    {{ record[column.key] || "-" }}
                  </template>
                </template>
              </a-table>
            </div>

            <div v-else class="table-responsive">
              <a-table
                :columns="creditColumns"
                :data-source="creditRows"
                :loading="loading"
                :pagination="false"
                row-key="id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'entry_date'">
                    {{ formatDate(record.entry_date) }}
                  </template>
                  <template v-else-if="column.key === 'customer'">
                    <div class="account-cell">
                      <strong>{{ record.customername || "-" }}</strong>
                      <small>{{ record.mobile || record.customerid }}</small>
                    </div>
                  </template>
                  <template v-else-if="column.key === 'reference'">
                    <div class="reference-cell">
                      <strong>{{ record.billno || record.reference_id || "-" }}</strong>
                      <small>{{ record.reference_type || record.entry_type }}</small>
                    </div>
                  </template>
                  <template v-else-if="column.key === 'payment_status'">
                    <span class="type-chip">{{ record.payment_status || record.entry_type }}</span>
                  </template>
                  <template v-else-if="column.key === 'due_date'">
                    {{ formatDate(record.due_date) }}
                  </template>
                  <template v-else-if="['debit', 'credit', 'balance'].includes(column.key)">
                    <span class="money-text">{{ formatCurrency(record[column.key]) }}</span>
                  </template>
                  <template v-else>
                    {{ record[column.key] || "-" }}
                  </template>
                </template>
              </a-table>
            </div>

            <div class="ledger-footer">
              <span>{{ paginationText }}</span>
              <div class="pager-actions">
                <button class="btn btn-outline-secondary btn-sm" type="button" :disabled="loading || page <= 1" @click="changePage(page - 1)">
                  Previous
                </button>
                <button class="btn btn-outline-secondary btn-sm" type="button" :disabled="loading || page >= totalPages" @click="changePage(page + 1)">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const emptySummary = () => ({
  total_debit: 0,
  total_credit: 0,
  closing_balance: 0,
});

export default {
  name: "ledger",
  data() {
    return {
      title: "Ledger",
      title1: "Finance and account transaction summary",
      loading: false,
      viewMode: "ledger",
      rows: [],
      creditRows: [],
      summary: emptySummary(),
      page: 1,
      pageSize: 25,
      total: 0,
      totalPages: 0,
      filters: {
        date_from: "",
        date_to: "",
        transaction_type: "",
        locationid: "",
        search: "",
      },
      transactionTypeOptions: [
        { value: "", text: "All entries" },
        { value: "SALE", text: "Sales" },
        { value: "PURCHASE", text: "Purchases" },
        { value: "SALES_RETURN", text: "Sales Returns" },
        { value: "PURCHASE_RETURN", text: "Purchase Returns" },
      ],
      columns: [
        { title: "Date", dataIndex: "transaction_date", key: "transaction_date", width: 120 },
        { title: "Type", dataIndex: "transaction_type", key: "transaction_type", width: 140 },
        { title: "Reference", key: "reference", width: 180 },
        { title: "Account", dataIndex: "account_name", key: "account_name", width: 220 },
        { title: "Payment", dataIndex: "paymentmode", key: "paymentmode", width: 120 },
        { title: "Debit", dataIndex: "debit", key: "debit", align: "right", width: 130 },
        { title: "Credit", dataIndex: "credit", key: "credit", align: "right", width: 130 },
        { title: "Remarks", dataIndex: "remarks", key: "remarks", width: 220 },
      ],
      creditColumns: [
        { title: "Date", dataIndex: "entry_date", key: "entry_date", width: 120 },
        { title: "Customer", key: "customer", width: 220 },
        { title: "Reference", key: "reference", width: 180 },
        { title: "Status", dataIndex: "payment_status", key: "payment_status", width: 120 },
        { title: "Due Date", dataIndex: "due_date", key: "due_date", width: 120 },
        { title: "Debit", dataIndex: "debit", key: "debit", align: "right", width: 130 },
        { title: "Credit", dataIndex: "credit", key: "credit", align: "right", width: 130 },
        { title: "Balance", dataIndex: "balance", key: "balance", align: "right", width: 130 },
        { title: "Narration", dataIndex: "narration", key: "narration", width: 220 },
      ],
    };
  },
  computed: {
    locationOptions() {
      const options = [{ value: "", text: "Current location" }];
      return options.concat((this.$store.getters.getLocations || []).map((location) => ({
        value: location.locationid,
        text: location.locationname,
      })));
    },
    paginationText() {
      if (!this.total) return this.viewMode === "credits" ? "No customer credit entries found" : "No ledger entries found";
      const start = (this.page - 1) * this.pageSize + 1;
      const end = Math.min(this.page * this.pageSize, this.total);
      return `Showing ${start}-${end} of ${this.total}`;
    },
  },
  mounted() {
    this.fetchLedger();
  },
  methods: {
    applyFilters() {
      this.page = 1;
      this.fetchCurrentView();
    },
    resetFilters() {
      this.filters = {
        date_from: "",
        date_to: "",
        transaction_type: "",
        locationid: "",
        search: "",
      };
      this.page = 1;
      this.fetchCurrentView();
    },
    changePage(nextPage) {
      if (nextPage < 1 || (this.totalPages && nextPage > this.totalPages)) return;
      this.page = nextPage;
      this.fetchCurrentView();
    },
    switchView(viewMode) {
      if (this.viewMode === viewMode) return;
      this.viewMode = viewMode;
      this.page = 1;
      this.fetchCurrentView();
    },
    fetchCurrentView() {
      return this.viewMode === "credits" ? this.fetchCustomerCredits() : this.fetchLedger();
    },
    async fetchLedger() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/ledger", {
          params: {
            page: this.page,
            page_size: this.pageSize,
            search: this.filters.search || undefined,
            transaction_type: this.filters.transaction_type || undefined,
            date_from: this.filters.date_from || undefined,
            date_to: this.filters.date_to || undefined,
            locationid: this.filters.locationid || undefined,
          },
        });
        this.rows = response.data.items || [];
        this.summary = response.data.summary || emptySummary();
        this.total = response.data.total || 0;
        this.totalPages = response.data.total_pages || 0;
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load ledger." });
      } finally {
        this.loading = false;
      }
    },
    async fetchCustomerCredits() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/ledger/customer-credits", {
          params: {
            page: this.page,
            page_size: this.pageSize,
            search: this.filters.search || undefined,
            date_from: this.filters.date_from || undefined,
            date_to: this.filters.date_to || undefined,
            locationid: this.filters.locationid || undefined,
          },
        });
        this.creditRows = response.data.items || [];
        const creditSummary = response.data.summary || {};
        this.summary = {
          total_debit: creditSummary.total_debit || 0,
          total_credit: creditSummary.total_credit || 0,
          closing_balance: creditSummary.outstanding || 0,
        };
        this.total = response.data.total || 0;
        this.totalPages = response.data.total_pages || 0;
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load customer credits." });
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      return `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    transactionLabel(value) {
      const option = this.transactionTypeOptions.find((item) => item.value === value);
      return option?.text || value || "-";
    },
  },
};
</script>

<style scoped>
.ledger-card {
  border: 0;
}
.ledger-header,
.ledger-toolbar,
.summary-grid,
.ledger-footer {
  margin-bottom: 18px;
}
.ledger-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.ledger-header h5 {
  margin: 0;
  color: #111827;
  font-size: 1.05rem;
}
.ledger-header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.86rem;
}
.ledger-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
}
.ledger-tabs button {
  min-height: 32px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  padding: 0 12px;
  font-size: 0.8rem;
  font-weight: 700;
}
.ledger-tabs button.active {
  background: #ffffff;
  color: #1d4ed8;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}
.ledger-toolbar {
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
}
.ledger-toolbar label {
  margin-bottom: 6px;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
}
.ledger-actions {
  min-height: 38px;
}
.ledger-action-btn {
  min-width: 82px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.summary-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
}
.summary-card span {
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
}
.summary-card strong {
  color: #111827;
  font-size: 1.18rem;
}
.summary-card.debit {
  border-left: 4px solid #2563eb;
}
.summary-card.credit {
  border-left: 4px solid #16a34a;
}
.summary-card.balance {
  border-left: 4px solid #f97316;
}
.reference-cell,
.account-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.reference-cell small,
.account-cell small {
  color: #94a3b8;
  font-size: 0.72rem;
}
.type-chip {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #334155;
  font-size: 0.74rem;
  font-weight: 700;
}
.money-text {
  font-weight: 700;
  color: #111827;
}
.ledger-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  color: #64748b;
  font-size: 0.84rem;
}
.pager-actions {
  display: flex;
  gap: 8px;
}
@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
  .ledger-footer {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
